import { z } from 'zod';
import { registry } from '../registry';
const skinIntegrityOptions = [
    { value: 'intact', label: 'Intact — no lesions' },
    { value: 'callus', label: 'Callus present' },
    { value: 'preUlcerative', label: 'Pre-ulcerative' },
    { value: 'lesion-wound', label: 'Lesion / Wound' },
];
const sensationOptions = [
    { value: 'intact', label: 'Intact' },
    { value: 'tingling', label: 'Tingling / paresthesia' },
    { value: 'diminished', label: 'Diminished' },
    { value: 'absent', label: 'Absent' },
];
const nailsOptions = [
    { value: 'normal', label: 'Normal' },
    { value: 'thickened', label: 'Thickened' },
    { value: 'ingrown', label: 'Ingrown' },
    { value: 'fungal', label: 'Fungal' },
];
export const baseEndocrineSchema = z
    .object({
    bgReading: z.string().optional().register(registry, { label: 'Blood Glucose Reading — mg/dL' }),
    bgTiming: z
        .string()
        .optional()
        .register(registry, {
        label: 'Time / Timing of Check',
        options: [
            { value: 'fasting', label: 'Fasting (pre-meal)' },
            { value: 'postPrandial', label: 'Post-prandial (2 hr)' },
            { value: 'bedtime', label: 'Bedtime' },
            { value: 'random', label: 'Random' },
        ],
    }),
    glucoseTrend: z
        .string()
        .optional()
        .register(registry, {
        label: 'Glucose Trend',
        options: [
            { value: 'stable', label: 'Stable — within parameters' },
            { value: 'improving', label: 'Improving' },
            { value: 'elevated', label: 'Elevated' },
            { value: 'low', label: 'Low — hypo risk' },
            { value: 'fluctuating', label: 'Fluctuating — monitor closely' },
        ],
    }),
    hypoglycemiaEpisode: z
        .string()
        .optional()
        .register(registry, {
        label: 'Hypoglycemia episode?',
        options: [
            { value: 'none', label: 'None' },
            { value: 'resolvedAtHome', label: 'Yes — resolved at home' },
            { value: 'er', label: 'Yes — 911 / ER' },
        ],
    }),
    hyperglycemiaEpisode: z
        .string()
        .optional()
        .register(registry, {
        label: 'Hyperglycemia episode?',
        options: [
            { value: 'none', label: 'None' },
            { value: 'managedPerProtocol', label: 'Yes — managed per protocol' },
            { value: 'mdNotified', label: 'Yes — MD notified' },
        ],
    }),
    ssHyperglycemia: z
        .array(z.string())
        .optional()
        .register(registry, {
        label: 'S/S of Hyperglycemia',
        options: [
            { value: 'thirst', label: 'Thirst' },
            { value: 'frequentUrination', label: 'Frequent urination' },
            { value: 'fatigue', label: 'Fatigue' },
            { value: 'blurredVision', label: 'Blurred vision' },
            { value: 'headache', label: 'Headache' },
            { value: 'dryMouth', label: 'Dry mouth' },
            { value: 'nausea', label: 'Nausea and vomiting' },
            { value: 'abdominalPain', label: 'Abdominal pain' },
            { value: 'confusion', label: 'Confusion' },
        ],
    }),
    ssHypoglycemia: z
        .array(z.string())
        .optional()
        .register(registry, {
        label: 'S/S of Hypoglycemia',
        options: [
            { value: 'shakiness', label: 'Shakiness' },
            { value: 'perspiration', label: 'Perspiration' },
            { value: 'palpitations', label: 'Palpitations' },
            { value: 'anxiety', label: 'Anxiety' },
            { value: 'hunger', label: 'Hunger' },
            { value: 'irritability', label: 'Irritability' },
            { value: 'headache', label: 'Headache' },
            { value: 'lightheadedness', label: 'Lightheadedness' },
            { value: 'weakness', label: 'Weakness' },
            { value: 'faintness', label: 'Faintness' },
            { value: 'confusion', label: 'Confusion' },
        ],
    }),
    diabeticMgmt: z
        .array(z.string())
        .optional()
        .register(registry, {
        label: 'Management',
        options: [
            { value: 'dietExercise', label: 'Diet / Exercise' },
            { value: 'oralHypoglycemic', label: 'Oral hypoglycemic' },
            { value: 'insulin', label: 'Insulin' },
        ],
    }),
    insulinAdminBy: z
        .array(z.string())
        .optional()
        .register(registry, {
        label: 'Insulin administered by',
        options: [
            { value: 'patient', label: 'Patient' },
            { value: 'caregiver', label: 'Caregiver' },
            { value: 'sn', label: 'SN' },
        ],
    }),
    leftSkinIntegrity: z
        .string()
        .optional()
        .register(registry, { label: 'Skin integrity', options: [...skinIntegrityOptions] }),
    leftSensation: z
        .string()
        .optional()
        .register(registry, { label: 'Sensation (monofilament / subjective)', options: [...sensationOptions] }),
    leftNails: z
        .string()
        .optional()
        .register(registry, { label: 'Nails', options: [...nailsOptions] }),
    rightSkinIntegrity: z
        .string()
        .optional()
        .register(registry, { label: 'Skin integrity', options: [...skinIntegrityOptions] }),
    rightSensation: z
        .string()
        .optional()
        .register(registry, { label: 'Sensation (monofilament / subjective)', options: [...sensationOptions] }),
    rightNails: z
        .string()
        .optional()
        .register(registry, { label: 'Nails', options: [...nailsOptions] }),
    narrative: z.string().optional().register(registry, { label: 'Narrative' }),
    changeFromLastVisit: z
        .string()
        .optional()
        .register(registry, {
        label: 'Change from last visit?',
        options: [
            { value: 'noChange', label: 'No change' },
            { value: 'changeNoted', label: 'Change noted' },
        ],
    }),
})
    .register(registry, { label: 'Endocrine' });
/** Card titles used by the Endocrine UI — single source for edit + read-only. */
export const endocrineCardTitles = {
    bloodGlucoseMonitoring: 'Blood Glucose Monitoring',
    glycemiaEvents: 'Hypoglycemia / Hyperglycemia Events Since Last Visit',
    diabeticFootAssessment: 'Diabetic Foot Assessment',
    diabeticManagement: 'Diabetic Management',
    clinicalNarrative: 'Endocrine Clinical Narrative',
    leftFoot: 'Left Foot',
    rightFoot: 'Right Foot',
};
